import { Layout } from 'react-grid-layout';
import { isEqual, pick } from 'lodash';
import { MnBoardCommonFields } from '@kentik/ui-shared/boards/boards';

export type PanelLayout = { sm: Omit<Layout, 'i'>; lg: Omit<Layout, 'i'> };

export interface QueryHistoryEntry {
  /** ISO 8601 timestamp */
  timestamp: string;
  /** mn_user.id of the user who made this change */
  user_id: string;
  /** LLM-generated summary of what changed (or fallback structured diff) */
  summary: string;
  /** Snapshot of config.data at this point in time */
  query: unknown;
  /** Snapshot of config.chart at this point in time */
  chart?: unknown;
  /** LLM-generated title for this query state */
  autoTitle: string;
  /** The user's prompt that generated this query state, can be blank */
  user_prompt: string;
}

export interface VariableMapping {
  variableName: string;
  filterField: string;
  operator?: string;
  disabled?: boolean;
}

export function isLayoutEqual(a: PanelLayout, b: PanelLayout): boolean {
  const compareKeys = ['sm.x', 'sm.y', 'sm.w', 'sm.h', 'lg.x', 'lg.y', 'lg.w', 'lg.h'];
  const normalizedA = pick(a, compareKeys);
  const normalizedB = pick(b, compareKeys);
  return isEqual(normalizedA, normalizedB);
}

// cut-down version of Query?
export interface QueryFieldsInput {
  measurement?: string | null;
  metrics?: unknown;
  dimensions?: unknown;
  filters?: { dimensions?: { filterGroups?: unknown } | null } | null;
  reAgg?: unknown;
  rollups?: unknown;
}

export type MnBoardPanelType = 'ude' | 'widget' | 'section' | 'text' | null;

export interface MnBoardPanel extends MnBoardCommonFields {
  board_id: string;
  parent_panel_id: string | null;
  layout: PanelLayout;
  history?: QueryHistoryEntry[];
  type: MnBoardPanelType;
  config: MnBoardCommonFields['config'] & {
    // panel is collapsed in the board view
    collapsed?: boolean;
    // a default panel from an automatically generated observation deck
    isDefault?: boolean;
    widgetName?: string;
    variableMappings?: VariableMapping[];
    deleted?: boolean;
    showTitle?: boolean;
    description?: string;
    chart?: unknown;
    data?: QueryFieldsInput;
    // TODO not clear why we have a separate title field in config vs the base level title - this may be a mistake!
    title?: string;
    query?: { query_title?: string };
  };
}

export interface PanelData extends MnBoardPanel {
  // if this is a draft panel and a saved panel exists, this will be the saved panel data for comparison
  savedPanel?: PanelData;
}
